import { ReactElement, useEffect, useState } from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	Dialog,
	FormattedText,
	Form,
	FormItem,
	TextInput,
	ValidationError,
	SecretValueInput,
	Toolbar
} from "client/Components";
import { useFormHandler, IFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import { isNotNullNorUndefined, isNotEmpty, doesNotExceedLength } from "client/Validation/rules";
import {
	VilansKickConnectionSaveModel,
	create,
	update,
	get,
	VilansKickConnectionListItemModel
} from "client/Api/VilansKickConfiguration/vilansKickConfiguration";
import { AlertManager, useAlerts} from "client/Hooks";

export type VilansKickConnectionDialogProps = {
	connectionId?: number;
	onAfterSave(item: VilansKickConnectionListItemModel): void;
	onClose(): void;
};

const validationConfiguration: IValidationConfiguration<VilansKickConnectionSaveModel> = createValidationConfiguration<VilansKickConnectionSaveModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

export const VilansKickConnectionDialog = (props: VilansKickConnectionDialogProps): ReactElement => {
	const {
		connectionId,
		onAfterSave,
		onClose
	} = props;

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const alertManager: AlertManager = useAlerts();

	const [model, setModel] = useState<VilansKickConnectionSaveModel>({
		name: "",
		apiKey: {
			containsNewValue: true,
			value: ""
		}
	});
	const formHandler: IFormHandler<VilansKickConnectionSaveModel> = useFormHandler<VilansKickConnectionSaveModel>(
		model,
		setModel,
		validationConfiguration
	);

	useEffect(
		() => {
			if (connectionId === undefined) {
				return undefined;
			}

			const loadConnection = async (signal: AbortSignal): Promise<void> => {
				setIsLoading(true);

				try {
					const { name } = await get(
						connectionId,
						signal
					);
					setModel({
						name,
						apiKey: {
							containsNewValue: false,
							value: ""
						}
					});

					setIsLoading(false);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			loadConnection(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, connectionId, onClose]
	);

	const isNewConnection: boolean = connectionId === undefined;

	let titleKey: LocalizationKey;
	let saveKey: LocalizationKey;
	if (isNewConnection) {
		titleKey = "_NewNameConnection";
		saveKey = "_Add";
	} else {
		titleKey = "_EditNameConnection";
		saveKey = "_Save";
	}

	const submitHandler = async (): Promise<void> => {
		if (isSaving) {
			return;
		}

		const validationResult: InputValidationResult = await formHandler.validation.validate();
		if (validationResult.kind === InputValidationResultKind.Invalid) {
			return;
		}

		setIsSaving(true);

		let id: number | undefined = connectionId;
		try {
			if (id === undefined) {
				id = (await create(model)).id;
			} else {
				await update(id, model);
			}

			onClose();
			onAfterSave({ id, name: model.name });
		} catch (e) {
			if (!formHandler.validation.tryProcessServerError(e)) {
				alertManager.showError(localize("_ChangesNotSaved"));
			}
			setIsSaving(false);
		}
	};

	return (
		<Dialog hasBusyIndicator={isLoading || isSaving} trackingId="vilans-kick-connection-dialog">
			<Dialog.Header
				title={
					<FormattedText
						text={localize(titleKey)}
						thingsToReplace={[
							{ what: "{{Name}}", filler: localize("_VilansKick")}
						]}
					/>
				}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_Name")}>
						<TextInput {...formHandler.for("name").attachToTextInput()} />
						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_ApiKey")}>
						<SecretValueInput
							{...formHandler.for("apiKey").attachToSecretValueInput()}
							containsInitialValue={!isNewConnection}
						/>
						<ValidationError {
							...formHandler.for("apiKey")
								.for("value")
								.attachToValidationError()
						} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize(saveKey)}
						onClick={submitHandler}
						trackingId="save"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
VilansKickConnectionDialog.displayName = "VilansKickConnectionDialog";